import React, { useState, useEffect } from "react";
import { Edit2, Check, Loader2, FileCode, FileText, ExternalLink, Globe } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useEditableTitle } from "@/hooks/useEditableTitle";
import { useTranslation } from "react-i18next";

interface EditableTitleRecord {
  id: string;
  name: string;
  template?: {
    id: string;
    name: string;
    isDeleted?: boolean;
  } | null;
}

interface EditableTitleProps {
  record: EditableTitleRecord;
  onTitleUpdate?: (newTitle: string) => void;
  onNavigateToTemplate?: (templateId: string) => void;
  onEditStart?: () => void;
  onEditEnd?: () => void;
  className?: string;
  size?: "sm" | "md" | "lg";
  appearance?: "default" | "compact";
  // Пропс для индикации публикации
  isPublished?: boolean;
}

export const EditableTitle: React.FC<EditableTitleProps> = ({
  record,
  onTitleUpdate,
  onNavigateToTemplate,
  onEditStart,
  onEditEnd,
  className = "",
  size = "md",
  appearance = "default",
  isPublished = false,
}) => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  // Check if we're on mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const {
    isEditing,
    isSaving,
    currentValue,
    error,
    startEditing,
    saveValue,
    inputRef,
    inputProps,
  } = useEditableTitle({
    initialValue: record.name,
    recordId: record.id,
    onSuccess: onTitleUpdate,
    onEditStart,
    onEditEnd,
    validation: {
      minLength: 1,
      maxLength: 100,
    },
  });

  const sizeClasses = {
    sm: "text-sm",
    md: "text-base sm:text-base",
    lg: "text-lg",
  };

  const spacingClasses = {
    default: "space-y-2 sm:space-y-3",
    compact: "space-y-1 sm:space-y-2",
  };

  const handleSave = () => {
    saveValue(inputProps.value as string);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isEditing) {
      startEditing();
    }
  };

  if (isEditing) {
    return (
      <div className={cn("flex-1 min-w-0 pt-0.5 sm:pt-0", spacingClasses[appearance], className)}>
        <div className="relative">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              {isPublished && <Globe size={16} className="text-brand-600 absolute left-3 top-1/2 -translate-y-1/2 z-10" />}
              <Input
                {...inputProps}
                ref={inputRef}
                disabled={isSaving}
                className={cn(
                  "font-semibold sm:font-medium",
                  sizeClasses[size],
                  error ? "border-destructive" : "",
                  isSaving ? "opacity-50" : "",
                  isPublished ? "pl-10" : ""
                )}
                aria-label={t('records.title.edit_aria')}
                placeholder={t('records.title.placeholder')}
              />

              {isSaving && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 size={16} className="animate-spin text-muted-foreground" />
                </div>
              )}
            </div>

            {/* Action button for mobile - only save button */}
            {isMobile && (
              <div className="flex items-center">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="p-1 h-8 w-8"
                  aria-label={t('records.title.save_label')}
                >
                  <Check size={14} className="text-green-600" />
                </Button>
              </div>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div id="title-error" className="mt-1 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Loading state message for screen readers */}
          {isSaving && (
            <div className="sr-only" aria-live="polite" aria-label={t('records.title.saving_label')}>
              {t('records.title.saving_label')}
            </div>
          )}
        </div>

        {/* Template info and published indicator - editing mode */}
        <div className="flex items-center text-xs sm:text-sm text-muted-foreground mt-1 gap-3">
          {record.template ? (
            record.template.isDeleted ? (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <FileCode size={12} className="text-muted-foreground sm:w-[14px] sm:h-[14px]" />
                <span className="truncate">{record.template.name} {t('records.title.template_deleted')}</span>
              </span>
            ) : (
              <button
                onClick={() => onNavigateToTemplate?.(record.template!.id)}
                className="flex items-center gap-1.5 text-brand-600 hover:text-brand-800 transition-colors"
                disabled={isSaving}
              >
                <FileCode size={12} className="text-brand-400 sm:w-[14px] sm:h-[14px]" />
                <span className="truncate">{record.template.name}</span>
                <ExternalLink size={10} className="sm:w-[12px] sm:h-[12px]" />
              </button>
            )
          ) : (
            <span className="flex items-center gap-1.5">
              <FileText size={12} className="text-muted-foreground sm:w-[14px] sm:h-[14px]" />
              <span>{t('records.title.no_template')}</span>
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex-1 min-w-0 pt-0.5 sm:pt-0", spacingClasses[appearance], className)}>
      <div className="group">
        <div className="relative">
          <h1
            className={cn(
              "font-semibold sm:font-medium leading-tight text-foreground cursor-pointer flex items-center gap-2",
              "hover:text-brand-600 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 rounded",
              sizeClasses[size]
            )}
            onClick={startEditing}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="heading"
            aria-level={1}
            aria-label={t('records.title.view_aria', { name: currentValue })}
          >
            {isPublished && <Globe size={18} className="text-brand-600 flex-shrink-0" />}
            {currentValue}
          </h1>

          {/* Edit icon - visible on hover for desktop */}
          <button
            onClick={startEditing}
            className={cn(
              "absolute -right-6 top-1/2 -translate-y-1/2 p-1 rounded",
              "opacity-0 group-hover:opacity-100 transition-opacity",
              "hover:bg-muted focus:opacity-100 focus:bg-muted",
              "focus:outline-none focus:ring-2 focus:ring-brand-500",
              isMobile ? "opacity-100" : ""
            )}
            aria-label={t('records.title.edit_label')}
          >
            <Edit2 size={14} className="text-muted-foreground hover:text-brand-600" />
          </button>
        </div>

        {/* Template info */}
        <div className="flex items-center text-xs sm:text-sm text-muted-foreground mt-1 gap-3">
          {record.template ? (
            record.template.isDeleted ? (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <FileCode size={12} className="text-muted-foreground sm:w-[14px] sm:h-[14px]" />
                <span className="truncate">{record.template.name} {t('records.title.template_deleted')}</span>
              </span>
            ) : (
              <button
                onClick={() => onNavigateToTemplate?.(record.template!.id)}
                className="flex items-center gap-1.5 text-brand-600 hover:text-brand-800 transition-colors"
              >
                <FileCode size={12} className="text-brand-400 sm:w-[14px] sm:h-[14px]" />
                <span className="truncate">{record.template.name}</span>
                <ExternalLink size={10} className="sm:w-[12px] sm:h-[12px]" />
              </button>
            )
          ) : (
            <span className="flex items-center gap-1.5">
              <FileText size={12} className="text-muted-foreground sm:w-[14px] sm:h-[14px]" />
              <span>{t('records.title.no_template')}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};